"use client";

import { useGetAvatar } from "@/features/avatars";
import { scaledPx } from "@/features/lomdaEditorV2/components/scene/lib/utils";
import { cn } from "@/lib/utils";
import {
    CANVAS_H,
    CANVAS_W,
} from "@/features/lomdaEditorV2/lib/elements/transform";
import type { AvatarElement } from "@/features/lomdaEditorV2/lib/elements/types";

const SHADOW_CLASS: Record<string, string> = {
    none: "",
    soft: "shadow-md",
    strong: "shadow-xl",
};

export function AvatarElementView({ element }: { element: AvatarElement }) {
    const { avatar } = element;
    const { data: avatarRow } = useGetAvatar(avatar.avatarRowId);
    const look = avatarRow?.looks.find((l) => l.heygenLookId === avatar.lookId);
    const previewUrl =
        avatar.previewImageUrl ??
        look?.previewImageUrl ??
        avatarRow?.previewImageUrl ??
        null;

    // Look previews are portrait headshots. In a wide (full-screen) box,
    // a centered cover crops to a mid-face band; bias the crop to the top so
    // the head stays in frame and it reads as a presenter. The real render
    // composites HeyGen's 16:9 video, so this only affects the editor preview.
    const t = element.transform;
    const boxAspect =
        t.height > 0 ? (t.width * CANVAS_W) / (t.height * CANVAS_H) : 1;
    const isWide = boxAspect > 1.2;

    const style = avatar.style ?? {};
    const radius = style.borderRadius ?? 50;
    const opacity = style.opacity ?? 1;
    const ringStyle = style.ringWidth
        ? {
              boxShadow: `0 0 0 ${scaledPx(style.ringWidth)} ${style.ringColor ?? "#fff"}`,
          }
        : undefined;

    return (
        <div
            // The avatar's HeyGen mp4 (or a fallback still) is composited by the
            // render engine from the per-scene `avatar` payload, so hide this
            // preview from the layer-1 screenshot to avoid a doubled avatar.
            data-render-layer="2"
            className={cn(
                "pointer-events-none h-full w-full select-none overflow-hidden bg-muted",
                SHADOW_CLASS[style.shadow ?? "soft"]
            )}
            style={{ borderRadius: `${radius}%`, opacity, ...ringStyle }}
        >
            {previewUrl && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={previewUrl}
                    alt={avatar.displayName ?? ""}
                    draggable={false}
                    className={cn(
                        "h-full w-full object-cover",
                        isWide ? "object-top" : "object-center"
                    )}
                />
            )}
        </div>
    );
}
